<script setup lang="ts">
	import { CircleHelp, Globe, Map, MapPin, Navigation2 } from 'lucide-vue-next'
	import { Ref, computed, onMounted, ref } from 'vue'
	import { useToast } from 'vue-toastification'
	import { GoogleMap, InfoWindow, Marker } from 'vue3-google-map'

	import TransitionWrapper from '@/components/TransitionWrapper.vue'
	import { Badge } from '@/components/ui/badge'
	import { Input } from '@/components/ui/input'
	import { Label } from '@/components/ui/label'
	import { Separator } from '@/components/ui/separator'
	import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
	import {
		Tooltip,
		TooltipContent,
		TooltipProvider,
		TooltipTrigger,
	} from '@/components/ui/tooltip'
	import useGoogleAddressAutofill, {
		waitForGoogleMaps,
	} from '@/composables/useGoogleAddressAutofill'
	import { Address } from '@/interfaces/address'

	export interface SimplifiedAddress extends Omit<Address, 'id' | 'type'> {}

	export interface AddressForm {
		street?: string
		country?: string
		state?: string
		city?: string
		zipcode?: string
		neighborhood?: string
		number?: string
		internalNumber?: string
		latitude?: number
		longitude?: number
		countryCode?: string
		fullAddress?: string
		hotelName?: string
	}

	interface Props {
		accessToken?: string
		defaultAddress?: Address
		disabled?: boolean
		onDisabledClass?: string
		autoCompletedAddress?: boolean
		types?: string[]
	}

	const props = withDefaults(defineProps<Props>(), {
		autoCompletedAddress: false,
	})

	const {
		googleKey,
		searchValue,
		addressForm,
		showAllInputs,
		mapCenter,
		markerPosition,
		activeView,
		streetViewAvailable,
		transformedForm,
		addressVuelidate,
		validForm,
		initAutocomplete,
		nearbyHotels,
	} = useGoogleAddressAutofill({
		defaultAddress: props.defaultAddress,
		types: props.types,
	})

	defineExpose({
		addressForm: transformedForm,
		addressVuelidate,
		validForm,
	} as {
		addressForm: AddressForm
		addressVuelidate: typeof addressVuelidate
		validForm: Ref<boolean>
	})

	const inputRef = ref()
	const toast = useToast()
	const hoveredHotel = ref<{ type: 'main' } | { type: 'nearby'; index: number } | null>(null)

	const handleMouseOver = (type: 'main' | 'nearby', index?: number) => {
		if (type === 'main') {
			hoveredHotel.value = { type: 'main' }
		} else if (type === 'nearby' && index !== undefined) {
			hoveredHotel.value = { type: 'nearby', index }
		}
	}

	const handleMouseOut = (type: 'main' | 'nearby', index?: number) => {
		if (!hoveredHotel.value) return
		if (type === 'main' && hoveredHotel.value.type === 'main') {
			hoveredHotel.value = null
		} else if (
			type === 'nearby' &&
			hoveredHotel.value.type === 'nearby' &&
			hoveredHotel.value.index === index
		) {
			hoveredHotel.value = null
		}
	}

	onMounted(async () => {
		const el = inputRef.value?.$el as HTMLInputElement | undefined
		if (!el) return
		try {
			await waitForGoogleMaps()
			await initAutocomplete(el)
		} catch {
			toast.error('No se pudo cargar Google Maps. Verifica tu conexión e intenta más tarde.')
		}
	})

	const streetViewSrc = (lat: number, lng: number) =>
		`https://www.google.com/maps/embed/v1/streetview?key=${googleKey}&location=${lat},${lng}&pitch=10&fov=80`

	const mapTypeId = computed(() =>
		activeView.value === 'satellite' ? 'satellite' : 'roadmap'
	)

	const inputErrorClass = 'border-destructive focus-visible:ring-destructive/30'
</script>

<template>
	<div class="flex flex-col xl:flex-row gap-6 relative">
		<!-- ─── Form ─── -->
		<form class="flex lg:flex-1 w-full flex-col gap-4">
			<TransitionWrapper show>
				<fieldset
					v-show="showAllInputs"
					class="flex flex-1 w-full flex-col gap-4"
					:disabled="disabled"
				>
					<!-- País -->
					<div class="space-y-1.5">
						<Label for="addr-country">
							País <span class="text-destructive">*</span>
						</Label>
						<Input
							id="addr-country"
							v-model="addressForm.country"
							placeholder="ej. México"
							autocomplete="shipping country-name"
							:class="addressVuelidate.country.$error ? inputErrorClass : ''"
							@blur="addressVuelidate.country.$touch()"
						/>
						<p
							v-if="addressVuelidate.country.$error"
							class="text-xs text-destructive"
						>
							{{ addressVuelidate.country.$errors[0]?.$message }}
						</p>
					</div>

					<!-- Estado -->
					<div class="space-y-1.5">
						<Label for="addr-state">
							Estado <span class="text-destructive">*</span>
						</Label>
						<Input
							id="addr-state"
							v-model="addressForm.state"
							placeholder="ej. Nuevo León"
							autocomplete="shipping address-level1"
							:class="addressVuelidate.state.$error ? inputErrorClass : ''"
							@blur="addressVuelidate.state.$touch()"
						/>
						<p
							v-if="addressVuelidate.state.$error"
							class="text-xs text-destructive"
						>
							{{ addressVuelidate.state.$errors[0]?.$message }}
						</p>
					</div>

					<!-- Ciudad -->
					<div class="space-y-1.5">
						<Label for="addr-city">
							Ciudad <span class="text-destructive">*</span>
						</Label>
						<Input
							id="addr-city"
							v-model="addressForm.city"
							placeholder="ej. Monterrey"
							autocomplete="shipping address-level2"
							:class="addressVuelidate.city.$error ? inputErrorClass : ''"
							@blur="addressVuelidate.city.$touch()"
						/>
						<p
							v-if="addressVuelidate.city.$error"
							class="text-xs text-destructive"
						>
							{{ addressVuelidate.city.$errors[0]?.$message }}
						</p>
					</div>

					<!-- Calle -->
					<div class="space-y-1.5">
						<Label for="addr-street">
							Calle <span class="text-destructive">*</span>
						</Label>
						<Input
							id="addr-street"
							v-model="addressForm.street"
							placeholder="ej. Av. Revolución"
							:class="addressVuelidate.street.$error ? inputErrorClass : ''"
							@blur="addressVuelidate.street.$touch()"
						/>
						<p
							v-if="addressVuelidate.street.$error"
							class="text-xs text-destructive"
						>
							{{ addressVuelidate.street.$errors[0]?.$message }}
						</p>
					</div>

					<!-- Colonia -->
					<div class="space-y-1.5">
						<Label for="addr-neighborhood">Colonia</Label>
						<Input
							id="addr-neighborhood"
							v-model="addressForm.neighborhood"
							placeholder="ej. Jardines de Alta Vista"
						/>
					</div>

					<!-- CP / Núm. ext / Núm. int -->
					<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div class="space-y-1.5">
							<Label for="addr-zip">
								Código postal <span class="text-destructive">*</span>
							</Label>
							<Input
								id="addr-zip"
								v-model="addressForm.zipcode"
								placeholder="ej. 64840"
								autocomplete="shipping postal-code"
								:class="addressVuelidate.zipcode.$error ? inputErrorClass : ''"
								@blur="addressVuelidate.zipcode.$touch()"
							/>
							<p
								v-if="addressVuelidate.zipcode.$error"
								class="text-xs text-destructive"
							>
								{{ addressVuelidate.zipcode.$errors[0]?.$message }}
							</p>
						</div>

						<div class="space-y-1.5">
							<Label for="addr-number">
								Núm. exterior <span class="text-destructive">*</span>
							</Label>
							<Input
								id="addr-number"
								v-model="addressForm.number"
								placeholder="ej. 1234"
								:class="addressVuelidate.number.$error ? inputErrorClass : ''"
								@blur="addressVuelidate.number.$touch()"
							/>
							<p
								v-if="addressVuelidate.number.$error"
								class="text-xs text-destructive"
							>
								{{ addressVuelidate.number.$errors[0]?.$message }}
							</p>
						</div>

						<div class="space-y-1.5">
							<Label for="addr-int">Núm. interior</Label>
							<Input
								id="addr-int"
								v-model="addressForm.internalNumber"
								placeholder="ej. Piso 3"
							/>
						</div>
					</div>
				</fieldset>
			</TransitionWrapper>
		</form>

		<!-- ─── Map & Street View panel ─── -->
		<div class="xl:w-96 flex flex-col gap-3">
			<!-- Address search -->
			<div class="space-y-1.5">
				<div class="flex items-center gap-1.5">
					<Label for="address-search">
						Ubicación
						<span class="text-destructive ml-0.5">*</span>
					</Label>
					<TooltipProvider :delay-duration="200">
						<Tooltip>
							<TooltipTrigger as-child>
								<CircleHelp
									class="w-3.5 h-3.5 text-muted-foreground cursor-help"
								/>
							</TooltipTrigger>
							<TooltipContent class="max-w-56 text-center">
								Escribe la dirección y selecciónala del listado. Los campos se
								llenarán automáticamente.
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
				<div class="relative flex items-center">
					<MapPin
						class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10"
					/>
					<Input
						id="address-search"
						ref="inputRef"
						v-model="searchValue"
						type="text"
						placeholder="Buscar dirección..."
						:disabled="disabled"
						class="pl-9 pr-28"
						:class="disabled ? onDisabledClass : ''"
					/>
					<Badge
						variant="secondary"
						class="absolute right-2 text-[10px] font-medium pointer-events-none gap-1 shrink-0"
					>
						<img
							src="https://www.gstatic.com/images/branding/product/1x/maps_16dp.png"
							alt=""
							class="w-3 h-3"
						/>
						Google Maps
					</Badge>
				</div>
			</div>

			<Tabs
				v-if="markerPosition"
				:model-value="activeView"
				@update:model-value="(v) => (activeView = v as typeof activeView)"
			>
				<TabsList class="w-full h-10 p-1 bg-muted/50 border rounded-xl gap-1">
					<TabsTrigger
						value="roadmap"
						class="flex-1 h-8 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
					>
						<span class="flex items-center gap-1.5">
							<Map class="w-3.5 h-3.5" />
							Mapa
						</span>
					</TabsTrigger>
					<TabsTrigger
						value="satellite"
						class="flex-1 h-8 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
					>
						<span class="flex items-center gap-1.5">
							<Globe class="w-3.5 h-3.5" />
							Satélite
						</span>
					</TabsTrigger>
					<TabsTrigger
						value="streetview"
						class="flex-1 h-8 rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
					>
						<span class="flex items-center gap-1.5">
							<Navigation2 class="w-3.5 h-3.5" />
							Fachada
						</span>
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div
				v-if="!markerPosition"
				class="hidden xl:flex flex-col items-center justify-center gap-3 h-64 rounded-xl border border-dashed bg-muted/30 text-muted-foreground"
			>
				<MapPin class="w-8 h-8 opacity-30" />
				<p class="text-xs text-center px-6 leading-relaxed">
					Busca una dirección para ver la ubicación y fachada del lugar
				</p>
			</div>

			<!-- GoogleMap: always mounted to load API; shown on roadmap/satellite tabs -->
			<div
				class="rounded-xl overflow-hidden border shadow-sm"
				:style="
					markerPosition && activeView !== 'streetview' ?
						'height: 280px'
					:	'width:1px; height:1px; opacity:0; pointer-events:none; position:absolute'
				"
			>
				<GoogleMap
					:api-key="googleKey"
					:center="mapCenter"
					:zoom="17"
					:tilt="activeView === 'satellite' ? 45 : 0"
					:map-type-id="mapTypeId"
					:map-type-control="false"
					:street-view-control="false"
					:fullscreen-control="false"
					:zoom-control="false"
					@click="hoveredHotel = null"
					style="width: 100%; height: 100%"
				>
					<Marker
						v-if="markerPosition"
						:options="{ position: markerPosition }"
						@mouseover="handleMouseOver('main')"
						@click="handleMouseOver('main')"
					>
						<InfoWindow v-if="hoveredHotel?.type === 'main'" @closeclick="hoveredHotel = null">
							<div class="select-none pointer-events-none min-w-[220px] max-w-[260px] flex flex-col font-sans">
								<div class="p-3 pb-2 pr-8 flex flex-col gap-0.5">
									<div class="flex items-center gap-1">
										<span class="material-symbols-outlined text-[14px] text-red-600">star</span>
										<span class="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Hotel Sugerido</span>
									</div>
									<p class="text-xs font-bold text-gray-900 leading-snug">
										{{ searchValue }}
									</p>
								</div>
								<div class="relative w-full h-[120px] bg-gray-100 border-t border-gray-150">
									<iframe
										:src="streetViewSrc(markerPosition.lat, markerPosition.lng)"
										class="w-full h-full border-none"
										allowfullscreen
										loading="lazy"
										referrerpolicy="no-referrer-when-downgrade"
									/>
								</div>
							</div>
						</InfoWindow>
					</Marker>

					<Marker
						v-for="(hotel, i) in nearbyHotels"
						:key="i"
						:options="{
							position: { lat: hotel.lat, lng: hotel.lng },
							icon: {
								url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
							}
						}"
						@mouseover="handleMouseOver('nearby', i)"
						@click="handleMouseOver('nearby', i)"
					>
						<InfoWindow v-if="hoveredHotel?.type === 'nearby' && hoveredHotel.index === i" @closeclick="hoveredHotel = null">
							<div class="select-none pointer-events-none min-w-[220px] max-w-[260px] flex flex-col font-sans">
								<div class="p-3 pb-2 pr-8 flex flex-col gap-0.5">
									<div class="flex items-center gap-1">
										<span class="material-symbols-outlined text-[14px] text-blue-600">hotel</span>
										<span class="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Hotel Cercano</span>
									</div>
									<p class="text-xs font-bold text-gray-900 leading-snug">
										{{ hotel.name }}
									</p>
								</div>
								<div class="relative w-full h-[120px] bg-gray-100 border-t border-gray-150">
									<iframe
										:src="streetViewSrc(hotel.lat, hotel.lng)"
										class="w-full h-full border-none"
										allowfullscreen
										loading="lazy"
										referrerpolicy="no-referrer-when-downgrade"
									/>
								</div>
							</div>
						</InfoWindow>
					</Marker>
				</GoogleMap>
			</div>

			<!-- Street View: shown on fachada tab -->
			<div
				v-if="markerPosition && activeView === 'streetview'"
				class="rounded-xl overflow-hidden border shadow-sm"
				style="height: 280px"
			>
				<iframe
					v-if="streetViewAvailable"
					:src="streetViewSrc(markerPosition.lat, markerPosition.lng)"
					style="width: 100%; height: 100%; border: none"
					allowfullscreen
					loading="lazy"
					referrerpolicy="no-referrer-when-downgrade"
				/>
				<div
					v-else
					class="w-full h-full flex flex-col items-center justify-center gap-3 bg-muted/40 text-muted-foreground"
				>
					<Navigation2 class="w-8 h-8 opacity-30" />
					<p class="text-xs text-center px-6">
						No hay imágenes de calle disponibles para esta ubicación
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
	:deep(.gm-style-iw-c) {
		padding: 0 !important;
		border-radius: 12px !important;
	}
	:deep(.gm-style-iw-d) {
		overflow: hidden !important;
		max-height: none !important;
	}
	:deep(.gm-ui-hover-effect) {
		top: 8px !important;
		right: 8px !important;
	}
</style>
